import { CommonModule } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { inject, Injector, ModuleWithProviders, NgModule, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

import { JwtModule } from '@auth0/angular-jwt';
import { DesignSystemModule } from '@compeople-shared/design-system-angular';
import { defineCustomElements as designSystemDefineElements } from '@compeople-intern/spamanager-icons/loader';
import { provideChdErrorMessages } from '@cp-produkte/cruise-hospitality-design';
import { TranslocoService } from '@jsverse/transloco';

import { ApiModule, Configuration } from '@api';
import { InterceptorModule } from '@interceptor';
import { TokenService } from '@permission';
import { PipesModule } from '@pipe';
import { ServicesModule } from '@services';
import { StateModule } from '@state';
import { I18Service, TranslocoRootModule } from '@translation';

import { environment } from '../environments/environment';
import { AppInjector } from './app-injector';
import { AppSpaRoutingModule } from './app-spa-routing.module';
import { AppComponent } from './app.component';
import { provideConfiguredChdBackend } from './chd-backend.provider';
import { ComponentsModule } from './components/components.module';
import { FeatureToggleService } from './permission/feature-toggle.service';

export function apiConfigurationFactory(): Configuration {
  const DOMAIN = '';
  return new Configuration({
    basePath: `${DOMAIN}${environment.config.api}`,
  });
}

export function apiModuleForRoot(): ModuleWithProviders<ApiModule> {
  return {
    ngModule: ApiModule,
    providers: [
      {
        provide: Configuration,
        useFactory: apiConfigurationFactory,
        deps: [],
      },
    ],
  };
}

export function initialize(i18Service: I18Service, featureToggleService: FeatureToggleService) {
  return async function () {
    await Promise.all([featureToggleService.loadFeatures(), i18Service.setActiveLanguage('en-EN')]);
  };
}

// Shared across all CHD form controls app-wide; field-specific cases (e.g. dynamic max caps)
// still override via the component's `[errorMessage]` input, which always takes precedence.
export function chdErrorMessageResolverFactory() {
  const translocoService = inject(TranslocoService);
  return (errors: Record<string, unknown>): string | null => {
    if (errors['required']) {
      return translocoService.translate('error.validation.required');
    }
    if (errors['min']) {
      return translocoService.translate('error.validation.min', { min: (errors['min'] as { min: number }).min });
    }
    if (errors['max']) {
      return translocoService.translate('error.validation.max', { max: (errors['max'] as { max: number }).max });
    }
    return null;
  };
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    CommonModule,
    BrowserModule,
    InterceptorModule.forRoot(),
    AppSpaRoutingModule,
    StateModule.forRoot(),
    ServicesModule.forRoot(),
    apiModuleForRoot(),
    TranslocoRootModule,
    ComponentsModule,
    DesignSystemModule,
    PipesModule,
    JwtModule.forRoot({
      config: {
        tokenGetter: () => {
          return AppInjector.injector.get(TokenService).token;
        },
        headerName: 'x-aida-authorization',
        skipWhenExpired: true,
      },
    }),
  ],
  providers: [
    provideAppInitializer(() => {
      const initializerFn = initialize(inject(I18Service), inject(FeatureToggleService));
      return initializerFn();
    }),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    provideChdErrorMessages(chdErrorMessageResolverFactory),
    provideConfiguredChdBackend(),
  ],
})
export class AppSpaModule {
  private injector = inject(Injector);

  constructor() {
    const injector = this.injector;

    AppInjector.injector = injector;
    designSystemDefineElements();
  }
}
